/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConversation } from '@elevenlabs/react';
import { elevenLabsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, 
  ArrowLeft, Loader2, AlertTriangle, Heart, Waves
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Voice visualization component
const VoiceWaveform = ({ isActive, isSpeaking }) => {
  const bars = 12;
  const [heights, setHeights] = useState(Array(bars).fill(8));
  
  useEffect(() => {
    if (!isActive && !isSpeaking) {
      setHeights(Array(bars).fill(8));
      return;
    }
    
    const interval = setInterval(() => {
      setHeights(Array(bars).fill(0).map(() => 
        isActive || isSpeaking ? Math.random() * 40 + 20 : 8
      ));
    }, 150);
    
    return () => clearInterval(interval);
  }, [isActive, isSpeaking]);
  
  return (
    <div className="flex items-center justify-center gap-1 h-16">
      {heights.map((height, i) => (
        <div
          key={i}
          className={`w-1 rounded-full transition-all duration-150 ${
            isActive ? 'bg-red-500' : isSpeaking ? 'bg-primary' : 'bg-muted'
          }`}
          style={{ height: `${height}px` }}
        />
      ))}
    </div>
  );
};

// Animated avatar for voice call
const VoiceAvatar = ({ state }) => {
  const ringClass = state === 'speaking' ? 'animate-pulse-ring' : 
                    state === 'listening' ? 'animate-breathe' : '';
  
  return (
    <div className="relative">
      {/* Outer rings */}
      <div className={`absolute inset-0 rounded-full bg-primary/10 scale-150 ${ringClass}`} />
      <div className={`absolute inset-0 rounded-full bg-primary/20 scale-125 ${ringClass}`} style={{ animationDelay: '0.2s' }} />
      
      {/* Main avatar */}
      <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-sage-300 to-sage-500 flex items-center justify-center shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-sage-600/30 to-transparent" />
        
        {/* Face */}
        <svg viewBox="0 0 100 100" className="w-24 h-24">
          {/* Eyes */}
          <ellipse 
            cx="35" cy="42" rx="6" ry={state === 'speaking' ? '4' : '8'} 
            fill="#2D3A3A" 
            className="transition-all duration-200" 
          />
          <ellipse 
            cx="65" cy="42" rx="6" ry={state === 'speaking' ? '4' : '8'} 
            fill="#2D3A3A" 
            className="transition-all duration-200"
          />
          {/* Eye shine */}
          <circle cx="33" cy="39" r="2" fill="white" opacity="0.8" />
          <circle cx="63" cy="39" r="2" fill="white" opacity="0.8" />
          {/* Mouth - animated based on state */}
          {state === 'speaking' ? (
            <ellipse cx="50" cy="65" rx="10" ry="8" fill="#2D3A3A" className="animate-pulse" />
          ) : state === 'listening' ? (
            <circle cx="50" cy="65" r="6" fill="#2D3A3A" />
          ) : (
            <path d="M 35 60 Q 50 75 65 60" stroke="#2D3A3A" strokeWidth="3" fill="none" strokeLinecap="round" />
          )}
        </svg>
      </div>
      
      {/* Status indicator */}
      <div className={`absolute bottom-2 right-2 w-5 h-5 rounded-full border-2 border-white ${
        state === 'speaking' ? 'bg-primary animate-pulse' :
        state === 'listening' ? 'bg-red-500 animate-pulse' :
        'bg-emerald-400'
      }`} />
    </div>
  );
};

const CrisisModal = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <div className="mx-auto w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-4">
          <Heart className="w-8 h-8 text-accent" />
        </div>
        <DialogTitle className="text-center text-xl">You're Not Alone</DialogTitle>
        <DialogDescription className="text-center">
          I've detected you may be going through something very difficult. Please reach out for support.
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="p-4 rounded-2xl bg-accent/10 border border-accent/20">
          <h4 className="font-semibold flex items-center gap-2 text-foreground mb-3">
            <Phone className="w-4 h-4 text-accent" />
            Crisis Resources
          </h4>
          <ul className="space-y-2 text-sm">
            <li><strong>988 Suicide & Crisis Lifeline:</strong> Call or text 988</li>
            <li><strong>Crisis Text Line:</strong> Text HOME to 741741</li>
          </ul>
        </div>
      </div>
      
      <Button onClick={onClose} className="w-full rounded-full" data-testid="crisis-modal-close">
        I Understand
      </Button>
    </DialogContent>
  </Dialog>
);

const VoiceCall = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [callState, setCallState] = useState('idle'); // idle, connecting, active, ended
  const [avatarState, setAvatarState] = useState('idle'); // idle, listening, speaking
  const [isMuted, setIsMuted] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [showCrisis, setShowCrisis] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [agentResponse, setAgentResponse] = useState('');
  
  const durationIntervalRef = useRef(null);
  
  // ElevenLabs Conversation hook
  const conversation = useConversation({
    onConnect: () => {
      console.log('ElevenLabs: Connected');
      setCallState('active');
      setError('');
      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    },
    onDisconnect: () => {
      console.log('ElevenLabs: Disconnected');
      if (callState !== 'idle') {
        setCallState('ended');
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    },
    onMessage: (message) => {
      console.log('ElevenLabs message:', message);
      // Handle different message types
      if (message.type === 'user_transcript') {
        setTranscript(message.text || '');
      } else if (message.type === 'agent_response') {
        setAgentResponse(message.text || '');
      }
    },
    onError: (err) => {
      console.error('ElevenLabs error:', err);
      setError(err.message || 'Connection error occurred');
      setCallState('idle');
    }
  });

  // Update avatar state based on conversation state
  useEffect(() => {
    if (conversation.isSpeaking) {
      setAvatarState('speaking');
    } else if (callState === 'active') {
      setAvatarState('listening');
    } else {
      setAvatarState('idle');
    }
  }, [conversation.isSpeaking, callState]);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch signed URL from backend (Supabase Edge Function)
  const fetchSignedUrl = async () => {
    try {
      const { data } = await elevenLabsAPI.getSignedUrl();
      if (!data?.signed_url) throw new Error('No signed URL returned');
      return data.signed_url;
    } catch (err) {
      console.error('Fetch signed URL error:', err);
      throw err;
    }
  };

  // Start the call
  const startCall = async () => {
    try {
      setCallState('connecting');
      setError('');
      setTranscript('');
      setAgentResponse('');
      
      // Request microphone permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Fetch signed URL
      const url = await fetchSignedUrl();
      setSignedUrl(url);
      
      // Start ElevenLabs conversation with signed URL
      await conversation.startSession({
        signedUrl: url
      });
      
    } catch (err) {
      console.error('Start call error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone access to use voice calls.');
      } else if (err.name === 'NotFoundError' || err.message.includes('device not found')) {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (err.message.includes('microphone')) {
        setError('Microphone error. Please check your microphone settings.');
      } else {
        setError(err.message || 'Failed to start call. Please try again.');
      }
      setCallState('idle');
    }
  };

  // End the call
  const endCall = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch (err) {
      console.error('End call error:', err);
    }
    
    setCallState('ended');
    setAvatarState('idle');
    
    // Clear timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
  }, [conversation]);

  // Toggle mute
  const toggleMute = useCallback(async () => {
    try {
      if (isMuted) {
        // Unmute - set volume to 1
        await conversation.setVolume({ volume: 1 });
      } else {
        // Mute - set volume to 0
        await conversation.setVolume({ volume: 0 });
      }
      setIsMuted(!isMuted);
    } catch (err) {
      console.error('Toggle mute error:', err);
    }
  }, [conversation, isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (conversation.status === 'connected') {
        conversation.endSession();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  // Determine if user is speaking (when not agent speaking and connected)
  const isUserSpeaking = callState === 'active' && !conversation.isSpeaking;

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      {/* Header */}
      <header className="p-3 sm:p-4 flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => {
            if (callState === 'active') endCall();
            navigate('/chat');
          }}
          className="rounded-full h-9 w-9 sm:h-10 sm:w-10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </Button>
        <h1 className="text-base sm:text-lg font-heading font-semibold">Voice Call</h1>
        <div className="w-9 sm:w-10" />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6">
        {/* Avatar */}
        <div className="mb-6 sm:mb-8">
          <VoiceAvatar state={avatarState} />
        </div>

        {/* Status Text */}
        <div className="text-center mb-6 sm:mb-8 space-y-1 sm:space-y-2">
          <h2 className="text-lg sm:text-xl font-heading font-semibold text-foreground">
            {callState === 'idle' && 'Ready to Talk?'}
            {callState === 'connecting' && 'Connecting...'}
            {callState === 'active' && 'TPOCARE AI'}
            {callState === 'ended' && 'Call Ended'}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {callState === 'idle' && 'Start a real-time voice conversation with your AI companion'}
            {callState === 'connecting' && 'Setting up your private session'}
            {callState === 'active' && (
              <span className="flex items-center justify-center gap-2">
                <span className={`w-2 h-2 rounded-full ${conversation.isSpeaking ? 'bg-primary animate-pulse' : 'bg-red-500 animate-pulse'}`} />
                {conversation.isSpeaking ? 'AI is speaking...' : 'Listening to you...'}
                <span className="ml-2">{formatDuration(callDuration)}</span>
              </span>
            )}
            {callState === 'ended' && `Duration: ${formatDuration(callDuration)}`}
          </p>
        </div>

        {/* Voice Waveform */}
        {callState === 'active' && (
          <div className="mb-6 sm:mb-8">
            <VoiceWaveform isActive={isUserSpeaking} isSpeaking={conversation.isSpeaking} />
          </div>
        )}

        {/* Transcript/Response Display */}
        {callState === 'active' && (transcript || agentResponse) && (
          <Card className="w-full max-w-md mb-6 sm:mb-8 shadow-card border-0">
            <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3">
              {transcript && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">You said:</p>
                  <p className="text-xs sm:text-sm text-foreground">{transcript}</p>
                </div>
              )}
              {agentResponse && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">AI Response:</p>
                  <p className="text-xs sm:text-sm text-foreground">{agentResponse}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl bg-destructive/10 text-destructive text-xs sm:text-sm flex items-center gap-2 max-w-md">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Call Controls */}
        <div className="flex items-center gap-4 sm:gap-6">
          {callState === 'idle' && (
            <Button
              size="lg"
              onClick={startCall}
              className="rounded-full h-14 w-14 sm:h-16 sm:w-16 bg-emerald-500 hover:bg-emerald-600"
              data-testid="start-call-btn"
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
          )}
          
          {callState === 'connecting' && (
            <Button
              size="lg"
              disabled
              className="rounded-full h-14 w-14 sm:h-16 sm:w-16"
            >
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
            </Button>
          )}
          
          {callState === 'active' && (
            <>
              {/* Mute/Unmute Button */}
              <Button
                variant="outline"
                size="lg"
                onClick={toggleMute}
                className={`rounded-full h-12 w-12 sm:h-14 sm:w-14 ${isMuted ? 'bg-destructive/10 border-destructive' : ''}`}
                data-testid="mute-btn"
              >
                {isMuted ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
              
              {/* Active Call Indicator */}
              <div className={`rounded-full h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center transition-all ${
                conversation.isSpeaking 
                  ? 'bg-primary/20 border-2 border-primary' 
                  : 'bg-red-500/20 border-2 border-red-500'
              }`}>
                <Waves className={`w-6 h-6 sm:w-8 sm:h-8 ${conversation.isSpeaking ? 'text-primary' : 'text-red-500'} ${!conversation.isSpeaking ? 'animate-pulse' : ''}`} />
              </div>
              
              {/* End Call Button */}
              <Button
                variant="destructive"
                size="lg"
                onClick={endCall}
                className="rounded-full h-12 w-12 sm:h-14 sm:w-14"
                data-testid="end-call-btn"
              >
                <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </>
          )}
          
          {callState === 'ended' && (
            <div className="flex gap-3 sm:gap-4">
              <Button
                variant="outline"
                onClick={() => {
                  setCallState('idle');
                  setCallDuration(0);
                  setTranscript('');
                  setAgentResponse('');
                  setError('');
                }}
                className="rounded-full text-sm"
                data-testid="new-call-btn"
              >
                New Call
              </Button>
              <Button
                onClick={() => navigate('/chat')}
                className="rounded-full text-sm"
                data-testid="back-to-chat-btn"
              >
                Back to Chat
              </Button>
            </div>
          )}
        </div>

        {/* Instructions */}
        {callState === 'active' && (
          <p className="mt-6 sm:mt-8 text-xs sm:text-sm text-muted-foreground text-center max-w-sm px-4">
            Just speak naturally. The AI will listen and respond in real-time.
          </p>
        )}
        
        {callState === 'idle' && (
          <div className="mt-6 sm:mt-8 text-center max-w-sm px-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-2">
              Have a natural conversation with your AI counsellor
            </p>
            <p className="text-xs text-muted-foreground/70">
              Powered by ElevenLabs Conversational AI
            </p>
          </div>
        )}
      </main>

      {/* Crisis Modal */}
      <CrisisModal isOpen={showCrisis} onClose={() => setShowCrisis(false)} />
    </div>
  );
};

export default VoiceCall;
